import React, { useState, useCallback, useRef } from 'react';

// 长按配置接口
interface LongPressOptions {
    shouldPreventDefault?: boolean;
    delay?: number;
}

// 事件类型
type LongPressEvent = React.MouseEvent | React.TouchEvent;

// 长按Hook返回类型
interface LongPressReturnType {
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
    onMouseUp: (e: React.MouseEvent) => void;
    onMouseLeave: (e: React.MouseEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
}

// 自定义长按Hook
const useLongPress = (
    onLongPress: (e: LongPressEvent) => void,
    onClick?: (e: LongPressEvent) => void,
    { shouldPreventDefault = true, delay = 300 }: LongPressOptions = {}
): LongPressReturnType => {
    const [longPressTriggered, setLongPressTriggered] = useState<boolean>(false);
    const timeout = useRef<NodeJS.Timeout>();
    const target = useRef<EventTarget>();

    const start = useCallback(
        (event: LongPressEvent) => {
            if (shouldPreventDefault && event.target) {
                event.target.addEventListener('touchend', preventDefault, {
                    passive: false
                });
                target.current = event.target;
            }
            timeout.current = setTimeout(() => {
                onLongPress(event);
                setLongPressTriggered(true);
            }, delay);
        },
        [onLongPress, delay, shouldPreventDefault]
    );

    const clear = useCallback(
        (event: LongPressEvent, shouldTriggerClick = true) => {
            timeout.current && clearTimeout(timeout.current);
            shouldTriggerClick && !longPressTriggered && onClick?.(event);
            setLongPressTriggered(false);
            if (shouldPreventDefault && target.current) {
                (target.current as HTMLElement).removeEventListener('touchend', preventDefault);
            }
        },
        [shouldPreventDefault, onClick, longPressTriggered]
    );

    return {
        onMouseDown: (e: React.MouseEvent) => start(e),
        onTouchStart: (e: React.TouchEvent) => start(e),
        onMouseUp: (e: React.MouseEvent) => clear(e),
        onMouseLeave: (e: React.MouseEvent) => clear(e, false),
        onTouchEnd: (e: React.TouchEvent) => clear(e)
    };
};

// 阻止默认行为
const preventDefault = (event: Event): void => {
    if (!isTouchEvent(event)) return;
    if ((event as TouchEvent).touches.length < 2 && event.preventDefault) {
        event.preventDefault();
    }
};

// 检查是否为触摸事件
const isTouchEvent = (event: Event): event is TouchEvent => {
    return 'touches' in event;
};



export default useLongPress;
